import React from 'react';
import { StyleSheetManager, ThemeProvider } from 'styled-components';
import isPropValid from '@emotion/is-prop-valid';
import '@/styles/bs-font-face.css';
import GlobalStyle from '@/styles/global';
import { Provider } from 'react-redux';
import { HelmetProvider } from '@dr.pogodin/react-helmet';
import { I18nextProvider } from 'react-i18next';
import i18n from '@/i18n';
import { BrowserRouter } from 'react-router';
import { CustomThemeProvider, useTheme } from '@/core/themes/ThemeContext';
import store from '@/store/store';
import { IslandsContainer } from './IslandsContainer';
import { ModalHost } from './ModalHost';

function shouldForwardProp(propName: string, target: unknown) {
    if (typeof target === 'string') return isPropValid(propName);
    return true;
}

const IslandsContent: React.FC = () => {
    const { theme } = useTheme();
    return (
        <ThemeProvider theme={theme}>
            <GlobalStyle />
            {/*
             * BodyThemeStyles намеренно НЕ подключён. Islands-бандл
             * всегда грузится внутри легаси-страницы (Bootstrap-сайт
             * с собственным `<body>`-оформлением). Если рендерить
             * <BodyThemeStyles /> здесь, при смене темы дёргался бы фон
             * легаси-сайта. Темизацией легаси занимается ванильный слой
             * (resources/js/bx/theme-switcher.js ставит data-theme на <html>,
             * tokens.css переключает переменные) — постепенно, по мере
             * перевода легаси-CSS на var(--…).
             *
             * В отличие от App.tsx (там тот же импорт условный по
             * `import.meta.env.DEV` для dev-локалхоста), здесь dev/prod
             * не разделяем — islands-бандл и в деве запускается на
             * легаси-странице.
             *
             * Когда захочется распространить смену темы и на легаси
             * (legacy будет читать тот же CSS-vars-токен или подобное) —
             * добавить сюда строку:
             *
             *     <BodyThemeStyles />
             *
             * с импортом `import GlobalStyle, { BodyThemeStyles } from '@/styles/global';`.
             * Никаких других изменений не потребуется.
             */}
            <IslandsContainer />
            <ModalHost />
        </ThemeProvider>
    );
};

/**
 * BrowserRouter — нужен для компонентов, которые внутри Islands вызывают
 * хуки react-router (useLocation/useNavigate). BrowserRouter синхронизируется
 * с реальным `window.location`, что критично для ProfileIsland — он определяет
 * активный таб (me/tests/widgets) по URL.
 *
 * BrowserRouter сам по себе не перехватывает клики — это делают только
 * компоненты `<Link>`. Наша инфраструктура использует обычные `<a href>`
 * для навигации между страницами, поэтому переходы продолжают идти через
 * сервер. Внутри-островов навигация (TestResultIsland → 'testing') делается
 * через локальный state + `history.replaceState`.
 *
 * Маршрутов на уровне AppIslands нет — каждый Island использует useLocation
 * для чтения текущего URL, отдельные Islands могут содержать свои
 * <Routes>/<Route> для внутренней навигации (например, ProfileIsland).
 */
export const AppIslands: React.FC = () => (
    <StyleSheetManager shouldForwardProp={shouldForwardProp}>
        <Provider store={store}>
            <HelmetProvider>
                <I18nextProvider i18n={i18n}>
                    <CustomThemeProvider>
                        <BrowserRouter>
                            <IslandsContent />
                        </BrowserRouter>
                    </CustomThemeProvider>
                </I18nextProvider>
            </HelmetProvider>
        </Provider>
    </StyleSheetManager>
);
